// Small header chip showing the current guest handle with an edit action.
// Click the chip -> inline input -> Enter to save, Esc to cancel. Persists
// to sessionStorage via src/lib/handle.ts. This is the guest-only shim
// until real auth lands; once a session is present the badge is replaced
// by the authenticated user menu. Edits are rate-limited to once per 24h
// per tab (tracked in sessionStorage alongside the handle itself).

import { Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  formatCooldown,
  isValidHandle,
  markHandleChanged,
  msUntilNextHandleChange,
  readStoredHandle,
  resolveHandle,
  writeStoredHandle,
} from '~/lib/handle';

export function HandleBadge() {
  const [handle, setHandle] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cooldownMs, setCooldownMs] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Resolve once on mount (generates + stores if none yet).
  useEffect(() => {
    setHandle(resolveHandle(null));
    setCooldownMs(msUntilNextHandleChange());
  }, []);

  // While locked, tick once a minute so the countdown label stays fresh.
  useEffect(() => {
    if (cooldownMs <= 0) return;
    const id = setInterval(() => setCooldownMs(msUntilNextHandleChange()), 60 * 1000);
    return () => clearInterval(id);
  }, [cooldownMs]);

  // React to storage changes from other tabs so editing in one tab shows
  // up everywhere.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'pb_handle' && e.newValue) setHandle(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const startEdit = () => {
    if (msUntilNextHandleChange() > 0) return;
    setDraft(handle ?? '');
    setError(null);
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commit = () => {
    const trimmed = draft.trim();
    if (!isValidHandle(trimmed)) {
      setError('3-32 chars, letters, digits, or hyphens');
      return;
    }
    // No-op change: close without consuming the daily allowance.
    if (trimmed === handle) {
      setEditing(false);
      return;
    }
    writeStoredHandle(trimmed);
    markHandleChanged();
    setHandle(trimmed);
    setCooldownMs(msUntilNextHandleChange());
    setEditing(false);
    // Force-reload the room if we're in one so the new handle takes effect
    // on the server side.
    if (window.location.pathname.startsWith('/room/')) {
      window.location.reload();
    }
  };

  const cancel = () => {
    setDraft('');
    setError(null);
    setEditing(false);
  };

  if (!handle) return null;

  if (editing) {
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
            className="h-7 rounded-md border border-border bg-transparent px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            maxLength={32}
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={commit}
            className="text-xs font-medium text-primary hover:underline"
          >
            save
          </button>
          <button type="button" onClick={cancel} className="text-xs text-muted-foreground">
            esc
          </button>
        </div>
        {error && <span className="text-xs text-destructive">{error}</span>}
      </div>
    );
  }

  const locked = cooldownMs > 0;
  return (
    <button
      type="button"
      onClick={startEdit}
      disabled={locked}
      className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground hover:border-primary disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:text-muted-foreground disabled:hover:border-border"
      title={
        locked
          ? `You can change your name again in ${formatCooldown(cooldownMs)}`
          : 'Change your display name (once a day)'
      }
    >
      @{handle}
      <Pencil className="size-3" />
    </button>
  );
}

export function getCurrentHandle(): string | null {
  return readStoredHandle();
}
